import { useState, useMemo, useRef } from 'react'
import { isLanguageSpanish } from 'helpers/language'
import { Container } from './MultiSelect.styles'
import {
  ErrorContainer,
  focusBack,
  MultiSelectHeader,
  MultiSelectOptionsContainer
} from './helpers'
import { MultiSelectProps } from './typings'

const MultiSelect = ({
  name,
  selectedOptions = [],
  options,
  error,
  placeholder = 'Please select an option',
  disabled,
  onChange,
  onBlur,
  testId = ''
}: MultiSelectProps): JSX.Element => {
  const searchRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const textSelected = useMemo(
    () =>
      selectedOptions
        .map(option => {
          const item = options.find(it => it.value === option.value)
          if (item) {
            return isLanguageSpanish ? item.spanishLabel : item.englishLabel
          }
          return ''
        })
        .join(', '),
    [selectedOptions, options]
  )

  const openCloseOptions = () => {
    if (disabled) return
    const open = !isOpen
    setIsOpen(open)
    if (open) {
      focusBack(open, searchRef)
    }
  }

  let timeoutId: number

  const handleWrapperOnBlur = () => {
    /*
     * Following React Accessibility practices on tab navigation events:
     * https://reactjs.org/docs/accessibility.html#mouse-and-pointer-events
     * 100 ms added to prevent close options before focus occurs
     */
    timeoutId = setTimeout(() => {
      setIsOpen(false)
      const element = document.activeElement as HTMLInputElement
      element.blur()
      if (onBlur) onBlur()
    }, 100) as unknown as number
  }

  const handleWrapperOnFocus = () => {
    /*
     * The onBlur event is going to be called every time there is an option change.
     * This will prevent handleWrapperOnBlur from closing the component when user is still going through the options.
     */
    clearTimeout(timeoutId)
  }

  const isThereOptionsSelected = selectedOptions.length > 0
  const isAllSelected = options.length === selectedOptions.length

  return (
    <Container
      aria-disabled={disabled}
      aria-expanded={isOpen}
      aria-haspopup="true"
      aria-label={name}
      aria-invalid={!!error}
      role="combobox"
    >
      <MultiSelectHeader
        error={error}
        disabled={disabled}
        openCloseOptions={openCloseOptions}
        testId={testId}
        isOpen={isOpen}
        isThereOptionsSelected={isThereOptionsSelected}
        textSelected={textSelected}
        onChange={onChange}
        searchRef={searchRef}
        placeholder={placeholder}
      />
      {error && <ErrorContainer error={error} />}
      {isOpen && (
        <MultiSelectOptionsContainer
          handleWrapperOnBlur={handleWrapperOnBlur}
          handleWrapperOnFocus={handleWrapperOnFocus}
          searchRef={searchRef}
          setIsOpen={setIsOpen}
          selectedOptions={selectedOptions}
          isOpen={isOpen}
          name={name}
          options={options}
          isAllSelected={isAllSelected}
          onChange={onChange}
        />
      )}
    </Container>
  )
}

export default MultiSelect
